import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { DrawTradeOrdersComponent } from "./draw-trade-orders.component";
import { DrawExitOrderComponent } from "./exit-orders/draw-exit-order.component";
import { BracketTradePlotLineComponent } from "./bracket-orders/bracket-trade-plot-line.component";
import { TradePlotLineComponent } from "./trade-plot-line.component";

export class OrderBracketManagerComponent implements IComponent {
    public id = 'order-bracket-manager-' + StringHelpers.newGuid();

    /**
     * SelectedGroupId = null, means it is active position order.
     */
    public selectedGroupId: string | null = null;

    private base: IChartContainer;
    private lastPrice = 0;
    private allTradeOrders: Array<TradeOrderDataModel> = [];
    private chartStyle: ChartStyleSettingsModel | null = null;
    private evaluated = false;

    constructor(base: IChartContainer) {
        this.base = base;

        this.base.chartService.subscribeChartRequestingNewSymbolData(() => {
            this.remove();
            this.evaluated = false;
        });
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;

            const nonChartMode = this.chartStyle?.nonNormalChartMode();
            if (!this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry || nonChartMode) {
                this.remove();
            }
        });

        this.base.chartService.subscribeAllTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.allTradeOrders = tradeOrders;

            // Snapshot group IDs now, before updateTrades() (100ms timer) recreates plot lines.
            // Used by revalidateSelection() to detect which group is newly appeared.
            const snapshotGroupIds = new Set(this.getAllBracketGroups().map(g => g.model.id));

            setTimeout(() => {
                this.evaluate();
                this.revalidateSelection(snapshotGroupIds);
            }, 500);
        });

        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.lastPrice = lastPrice;
        });
    }

    public selectGroup(groupId: string | null, force = false) {
        if (this.ordersAndPositionBracketCount === 0 && !force) {
            return;
        }

        const exitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const groups = this.getAllBracketGroups();
        if (groupId && groupId === this.selectedGroupId) {
            this.selectedGroupId = null;
        } else {
            this.selectedGroupId = groupId;
        }

        if (groups.length == 1 && !exitOrderComp) {
            this.selectedGroupId = groups[0].model.id;
        }

        this.activeGroup();
    }

    private activeGroup() {
        const groups = this.getAllBracketGroups();

        this.setExitOrderBracketsEnabled(false);
        for (const group of groups) {
            this.deactivateGroup(group);
        }

        if (!this.selectedGroupId) {
            this.setExitOrderBracketsEnabled(true);
        } else {
            for (const group of groups) {
                if (group.model.id === this.selectedGroupId) {
                    this.activateGroup(group);
                }
            }
        }
    }

    public remove() {
        this.selectedGroupId = null;
        const groups = this.getAllBracketGroups();
        for (const group of groups) {
            this.deactivateGroup(group);
        }
    }

    private evaluateRetryCount = 0;
    public evaluate() {
        const exitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const drawTradeOrdersComp = this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent;
        const tradePlotLines = drawTradeOrdersComp?.tradePlotLines ?? [];

        // console.log('Evaluating ordersAndPositionBracketCount with', this.ordersAndPositionBracketCount);
        // console.log('Evaluating ordersAndPositionCount with', this.ordersAndPositionCount);

        if (this.evaluated || !exitOrderComp || !drawTradeOrdersComp) return;

        if (!drawTradeOrdersComp.initialize || tradePlotLines.length === 0) {
            if (this.evaluateRetryCount < 10) {
                this.evaluateRetryCount++;
                setTimeout(() => this.evaluate(), 200);
            }
            return;
        }

        this.evaluateRetryCount = 0;
        this.evaluated = true;
        const exitOrderPlotLine = exitOrderComp?.exitOrderPlotLine;
        const exitOrderExists = !!exitOrderPlotLine;

        const groups = this.getBracketGroups(tradePlotLines);
        if (this.ordersAndPositionBracketCount == 0) {
            for (const group of groups) {
                this.activateGroup(group);
            }

            if (exitOrderExists) {
                this.setExitOrderBracketsEnabled(true);
            }
            return;
        }

        if (exitOrderExists) {
            for (const group of groups) {
                this.deactivateGroup(group);
            }
            this.setExitOrderBracketsEnabled(true);
            return;
        }

        // Check if the remembered selection is still present
        if (this.selectedGroupId) {
            const selectedGroup = groups.find(g => g.model.id === this.selectedGroupId);
            if (selectedGroup) {
                this.activateGroup(selectedGroup);
                for (const group of groups) {
                    if (group !== selectedGroup) {
                        this.deactivateGroup(group);
                    }
                }
                this.setExitOrderBracketsEnabled(false);
                return;
            }
            // Selection no longer exists — fall through to auto-select
            this.selectedGroupId = null;
        }

        // Auto-select: only consider groups that already have PT or SL drawn
        const withBrackets = this.getBracketGroupsWithBrackets(groups);

        if (withBrackets.length === 0) {
            if (groups.length === 1) {
                this.selectedGroupId = groups[0].model.id;
                this.activateGroup(groups[0]);
                this.setExitOrderBracketsEnabled(false);
            } else {
                for (const group of groups) {
                    this.deactivateGroup(group);
                }
                this.setExitOrderBracketsEnabled(true);
            }
            return;
        }

        let winner: BracketTradePlotLineComponent;

        if (withBrackets.length === 1) {
            winner = withBrackets[0];
        } else {
            // Pick the group whose order price is closest to last price
            winner = withBrackets.reduce((closest, group) => {
                const closestDiff = Math.abs(closest.model.price - this.lastPrice);
                const groupDiff = Math.abs(group.model.price - this.lastPrice);
                return groupDiff < closestDiff ? group : closest;
            });
        }

        this.selectedGroupId = winner.model.id;

        this.activateGroup(winner);
        for (const group of groups) {
            if (group !== winner) {
                this.deactivateGroup(group);
            }
        }

        this.setExitOrderBracketsEnabled(false);
    }

    // When a broker cancels and re-creates an order after a move (changing the ID),
    // snapshotGroupIds captures what existed before the recreation so we can identify
    // the new group that appeared in its place.
    private revalidateSelection(snapshotGroupIds: Set<string>) {
        const groups = this.getAllBracketGroups();

        const exitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const exitOrderPlotLine = exitOrderComp?.exitOrderPlotLine;
        const exitOrderExists = !!exitOrderPlotLine;
        
        // console.log('Revalidating selection with ordersAndPositionBracketCount', this.ordersAndPositionBracketCount);
        // console.log('this.selectedGroupId', this.selectedGroupId);

        if (this.ordersAndPositionBracketCount == 0) {
            for (const group of groups) {
                this.activateGroup(group);
            }

            if (exitOrderExists) {
                this.setExitOrderBracketsEnabled(true);
            }
            return;
        }

        if (!this.selectedGroupId) {
            for (const group of groups) {
                this.deactivateGroup(group);
            }

            if (exitOrderExists) {
                this.setExitOrderBracketsEnabled(true);
            }

            return;
        }

        if (groups.length === 0) return;

        const existingSelectedGroup = groups.find(g => g.model.id === this.selectedGroupId);
        if (existingSelectedGroup) {
            for (const group of groups) {
                if (group === existingSelectedGroup) {
                    this.activateGroup(group);
                } else {
                    this.deactivateGroup(group);
                }
            }

            if (exitOrderExists) {
                this.setExitOrderBracketsEnabled(false);
            }

            return;
        }

        // Groups that weren't in the snapshot are newly created replacements
        const newGroups = groups.filter(g => !snapshotGroupIds.has(g.model.id));

        const winner = newGroups.length === 1 ? newGroups[0]
            : groups.length === 1 ? groups[0]
                : null;

        if (!winner) return;

        this.selectedGroupId = winner.model.id;
        this.activeGroup();
    }

    private getAllBracketGroups(): Array<BracketTradePlotLineComponent> {
        const tradePlotLines = (this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent)?.tradePlotLines ?? [];
        return this.getBracketGroups(tradePlotLines);
    }

    private getBracketGroups(tradePlotLines: Array<TradePlotLineComponent>): Array<BracketTradePlotLineComponent> {
        const groups: Array<BracketTradePlotLineComponent> = [];
        for (const tpl of tradePlotLines) {
            if (tpl?.bracketTradePlotline) {
                groups.push(tpl.bracketTradePlotline);
            }
        }
        return groups;
    }

    private getBracketGroupsWithBrackets(groups: Array<BracketTradePlotLineComponent>): Array<BracketTradePlotLineComponent> {
        return groups.filter(g => g.profitTargetPlotLine !== null || g.stopLossPlotLines.length > 0);
    }

    public get ordersAndPositionCount(): number {
        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const exitOrderPlotLine = drawExitOrderComp?.exitOrderPlotLine;
        const tradePlotLines = (this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent)?.tradePlotLines ?? [];

        let total = tradePlotLines.length;

        if (exitOrderPlotLine) {
            total += 1;
        }

        return total;
    }

    public get ordersAndPositionBracketCount(): number {
        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const exitOrderPlotLine = drawExitOrderComp?.exitOrderPlotLine;
        const tradePlotLines = (this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent)?.tradePlotLines ?? [];

        let total = tradePlotLines.filter(tpl => tpl.bracketTradePlotline && tpl.bracketTradePlotline.hasBracketOrder).length;

        if (exitOrderPlotLine && exitOrderPlotLine.hasBracketOrder) {
            total += 1;
        }

        return total;
    }

    private activateGroup(group: BracketTradePlotLineComponent) {
        group.activate(this.allTradeOrders);
        group.profitTargetPlotLine?.setBracketEnabled(true);
        group.stopLossPlotLines.forEach(s => s.setBracketEnabled(true));
    }

    private deactivateGroup(group: BracketTradePlotLineComponent) {
        group.deactivate();
        group.profitTargetPlotLine?.setBracketEnabled(false);
        group.stopLossPlotLines.forEach(s => s.setBracketEnabled(false));
    }

    private setExitOrderBracketsEnabled(enabled: boolean) {
        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        const exitOrderPlotLine = drawExitOrderComp?.exitOrderPlotLine;
        if (!exitOrderPlotLine) return;
        if (enabled) {
            exitOrderPlotLine.enable?.();
            exitOrderPlotLine.profitTargetPlotLine?.enable();
            exitOrderPlotLine.stopLossPlotLines?.forEach((s: any) => s.enable());
        } else {
            exitOrderPlotLine.disable?.();
            exitOrderPlotLine.profitTargetPlotLine?.disable();
            exitOrderPlotLine.stopLossPlotLines?.forEach((s: any) => s.disable());
        }
        exitOrderPlotLine.profitTargetPlotLine?.setBracketEnabled(enabled);
        exitOrderPlotLine.stopLossPlotLines?.forEach((s: any) => s.setBracketEnabled(enabled));
    }
}
